/**
 * useFinanceAuditView — Issue #402.
 *
 * 调用 `/api/v1/finance/audit-view` 系列接口（typed via @jinzhuan/api-client）。
 * 不允许前端伪造审计 / reveal / 导出状态——所有数据来自后端。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
  type ApiComponents,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type FinanceAuditEntryDto =
  ApiComponents['schemas']['FinanceAuditEntryDto'];
export type FinanceAuditListResponseDto =
  ApiComponents['schemas']['FinanceAuditListResponseDto'];
export type FinanceAuditViewSourceStatusDto =
  ApiComponents['schemas']['FinanceAuditViewSourceStatusDto'];
export type FinanceAuditViewScopeDto =
  ApiComponents['schemas']['FinanceAuditViewScopeDto'];
export type RevealFinanceAuditEntryDto =
  ApiComponents['schemas']['RevealFinanceAuditEntryDto'];
export type RevealFinanceAuditEntryResponseDto =
  ApiComponents['schemas']['RevealFinanceAuditEntryResponseDto'];

export const FINANCE_AUDIT_SOURCE_TYPES = [
  'payment_record',
  'invoice_record',
  'installment_item',
  'finance_adjustment',
  'payment_application',
  'employee_advance',
  'expense_reimbursement',
  'advance_offset',
  'commission_ledger',
  'channel_commission_item',
  'payroll_row',
  'payroll_payout',
  'admin_procurement',
  'settlement_event',
] as const;

export type FinanceAuditSourceType = (typeof FINANCE_AUDIT_SOURCE_TYPES)[number];

export interface FinanceAuditListFilter {
  readonly sourceTypes?: ReadonlyArray<FinanceAuditSourceType>;
  readonly occurredAfter?: string;
  readonly occurredBefore?: string;
  readonly page?: number;
  readonly limit?: number;
}

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiClientError) {
    if (err.status === 401) return { message: '会话已失效', code: 'UNAUTHENTICATED' };
    if (err.status === 403) return { message: '没有权限', code: 'FORBIDDEN' };
    if (err.status === 404) return { message: '记录不存在', code: 'NOT_FOUND' };
    if (err.status === 400) return { message: '请求参数错误', code: 'BAD_REQUEST' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export function useFinanceAuditView() {
  const [data, setData] = useState<FinanceAuditListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter: FinanceAuditListFilter = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET('/api/v1/finance/audit-view', {
        params: {
          query: {
            sourceTypes: filter.sourceTypes
              ? [...filter.sourceTypes]
              : undefined,
            occurredAfter: filter.occurredAfter,
            occurredBefore: filter.occurredBefore,
            page: filter.page,
            limit: filter.limit,
          },
        },
      });
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useFinanceAuditReveal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const reveal = useCallback(
    async (
      body: RevealFinanceAuditEntryDto,
    ): Promise<RevealFinanceAuditEntryResponseDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/finance/audit-view/reveal',
          { body },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, reveal };
}

// 导出通过 authenticated fetch，与 SalesReportPage 相同模式——typed apiClient
// 的 GET 默认期望 JSON 响应，无法直接返回 CSV blob，因此沿用既有 fetch+Blob
// 模式，并通过 Authorization header 把同一个 token 传给后端。
export async function downloadFinanceAuditViewCsv(args: {
  reason: string;
  filter?: FinanceAuditListFilter;
}): Promise<void> {
  const token = getStoredAccessToken();
  if (!token) {
    throw new Error('未登录或会话已失效，请重新登录后再导出');
  }
  const params = new URLSearchParams();
  params.set('reason', args.reason);
  params.set('format', 'csv');
  if (args.filter?.page) params.set('page', String(args.filter.page));
  if (args.filter?.limit) params.set('limit', String(args.filter.limit));
  if (args.filter?.occurredAfter)
    params.set('occurredAfter', args.filter.occurredAfter);
  if (args.filter?.occurredBefore)
    params.set('occurredBefore', args.filter.occurredBefore);
  if (args.filter?.sourceTypes) {
    for (const t of args.filter.sourceTypes) {
      params.append('sourceTypes', t);
    }
  }
  const response = await fetch(
    `/api/v1/finance/audit-view/export?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/csv',
      },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw new Error(`导出失败：${response.status}`);
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('text/csv')) {
    throw new Error('导出响应类型异常，未取得 CSV 文件');
  }
  const blob = await response.blob();
  const filename =
    extractFilename(response.headers.get('content-disposition')) ??
    `finance-audit-view-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function extractFilename(headerValue: string | null): string | null {
  if (!headerValue) return null;
  const match = /filename="?([^";]+)"?/.exec(headerValue);
  return match && match[1] ? match[1] : null;
}
